import React from 'react';
import { toast } from 'react-toastify';
import { useGetDocumentsByLoanApplication, useGetVerifications } from '../../services/loanService';
import { downloadDocument } from '../../services/documentService';
import { VerificationStatus } from '../../services/schemas/loanServiceSchemas/verificationStatus';
import { VerificationItem } from '../../services/schemas/loanServiceSchemas/verificationItem';
import { DocumentReference } from '../../services/schemas/loanServiceSchemas/documentReference';
import { formatDateTime } from '../../utils/formatters';
import { getCheckCode, getCheckDocuments } from '../../constants/verificationChecks';
import DocumentActionsMenu from './DocumentActionsMenu';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreview';

/**
 * What verification concluded, for everyone downstream of it.
 *
 * An officer can pass a verification on with individual items rejected -- the entity checks out
 * but one document does not -- and that judgement is the most useful thing credit inherits. It
 * was previously readable only on the verification screen itself, by whoever held the task, so
 * the rejection travelled in the data and nowhere on screen.
 *
 * Read-only by design: this shows a decision someone else made, it does not offer to change it.
 */

interface VerificationSummaryProps {
  applicationId?: string;
  /** Collapsed by default where it is supporting context rather than the point of the page. */
  defaultOpen?: boolean;
}

const STATUS_STYLES: Record<string, { label: string; className: string; icon: string }> = {
  VERIFIED: { label: 'Verified', className: 'bg-success-subtle text-success-emphasis', icon: 'fa-check' },
  REJECTED: { label: 'Rejected', className: 'bg-danger-subtle text-danger-emphasis', icon: 'fa-times' },
  PENDING: { label: 'Pending', className: 'bg-secondary-subtle text-secondary-emphasis', icon: 'fa-clock-o' },
  NOT_PROVIDED: { label: 'Not provided', className: 'bg-warning-subtle text-warning-emphasis', icon: 'fa-minus' }
};

const StatusBadge: React.FC<{ status?: VerificationStatus }> = ({ status }) => {
  const style = STATUS_STYLES[String(status || 'PENDING')] || STATUS_STYLES.PENDING;
  return (
    <span className={`badge rounded-pill ${style.className}`}>
      <i className={`fa ${style.icon} me-1`}></i>
      {style.label}
    </span>
  );
};

const VerificationSummary: React.FC<VerificationSummaryProps> = ({ applicationId, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  const { data: categories, isLoading } = useGetVerifications(
    applicationId || '',
    { query: { enabled: !!applicationId } }
  );

  // The documents behind each check, so credit can read what the officer ruled on without
  // leaving the page. Same query as the verification screen's, so it is usually cached already.
  const { data: documents } = useGetDocumentsByLoanApplication(
    applicationId || '',
    undefined,
    { query: { enabled: !!applicationId } }
  );
  const documentPreview = useDocumentPreview(message => toast.error(message));
  const [downloadingKey, setDownloadingKey] = React.useState<string | null>(null);

  const handleDownload = async (documentKey: string, name?: string) => {
    setDownloadingKey(documentKey);
    try {
      const blob = await downloadDocument(documentKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingKey(null);
    }
  };

  const renderCheckDocuments = (item: VerificationItem) => {
    const checkDocuments: DocumentReference[] = getCheckDocuments(getCheckCode(item), documents);
    if (checkDocuments.length === 0) return null;
    return (
      <div className="d-flex flex-column gap-1 mt-1">
        {checkDocuments.map(doc => {
          const documentKey = doc.documentKey || doc.id || '';
          return (
            <div key={doc.id || documentKey} className="d-flex align-items-center gap-2 flex-wrap">
              <small className="text-body-secondary">
                <i className="fa fa-file-o me-1"></i>
                {doc.name || 'Unnamed document'}
                {doc.directorName && ` · ${doc.directorName}`}
              </small>
              <DocumentActionsMenu
                id={`summary-${item.id || getCheckCode(item)}-${doc.id || documentKey}`}
                actions={[
                  {
                    label: 'View',
                    icon: 'fa-eye',
                    busy: documentPreview.openingKey === documentKey,
                    onClick: () => documentPreview.open(documentKey, doc.name)
                  },
                  {
                    label: 'Download',
                    icon: 'fa-download',
                    busy: downloadingKey === documentKey,
                    onClick: () => handleDownload(documentKey, doc.name)
                  }
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (!applicationId || isLoading) return null;
  if (!categories || categories.length === 0) return null;

  const allItems = categories.flatMap(category => category.items || []);
  const rejected = allItems.filter(item => item.status === 'REJECTED');
  const verified = allItems.filter(item => item.status === 'VERIFIED');
  const outstanding = allItems.filter(
    item => item.status !== 'VERIFIED' && item.status !== 'REJECTED'
  );

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-shield me-2"></i>
          Verification outcome
        </h6>
        <div className="d-flex align-items-center gap-2">
          {rejected.length > 0 && (
            <span className="badge rounded-pill bg-danger-subtle text-danger-emphasis">
              {rejected.length} rejected
            </span>
          )}
          {outstanding.length > 0 && (
            <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis">
              {outstanding.length} outstanding
            </span>
          )}
          <span className="badge rounded-pill bg-success-subtle text-success-emphasis">
            {verified.length} verified
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setOpen(value => !value)}
            aria-expanded={open}
          >
            {open ? 'Hide detail' : 'Show detail'}
          </button>
        </div>
      </div>

      {/* A rejection is the thing credit most needs to see, so it reads without expanding. */}
      {rejected.length > 0 && (
        <div className="card-body pb-0">
          <div className="alert alert-danger mb-3">
            <div className="fw-semibold mb-2">
              <i className="fa fa-exclamation-triangle me-2"></i>
              Verification passed these on with items rejected
            </div>
            <ul className="mb-0 small">
              {rejected.map(item => (
                <li key={item.id || item.type}>
                  {item.label || item.type}
                  {item.notes && <span className="text-muted"> &mdash; {item.notes}</span>}
                  {item.verifiedByName && <span className="text-muted"> (rejected by {item.verifiedByName})</span>}
                  {renderCheckDocuments(item)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {open && (
        <div className="list-group list-group-flush">
          {categories.map(category => (
            <div key={category.categoryCode} className="list-group-item">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold small">{category.title || category.categoryCode}</span>
                <small className="text-muted">
                  {category.verifiedCount ?? 0} of {category.totalCount ?? (category.items || []).length} verified
                </small>
              </div>
              <div className="d-flex flex-column gap-2">
                {(category.items || []).map(item => (
                  <div
                    key={item.id || item.type}
                    className="d-flex justify-content-between align-items-start gap-3 flex-wrap"
                  >
                    <div className="small">
                      <div>{item.label || item.type}</div>
                      {item.notes && <div className="text-muted">{item.notes}</div>}
                      {/* By name only: verifiedBy is a user id, which means nothing on screen. */}
                      {item.status !== 'PENDING' && item.verifiedByName && (
                        <div className="text-muted">
                          <i className="fa fa-user-o me-1"></i>
                          {item.verifiedByName}
                          {item.verifiedAt ? ` · ${formatDateTime(item.verifiedAt)}` : ''}
                        </div>
                      )}
                      {renderCheckDocuments(item)}
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default VerificationSummary;
